import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api, unwrapApiResponse } from '@/utils/api';
import Navbar from '@/components/layout/Navbar';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import FormatoPicker from '@/components/FormatoPicker';
import { Heart, BookOpen, Play, List, Download, Settings2, Trash2, Check } from 'lucide-react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

// Tabs Components
import FavoritesTab from './colecao/components/FavoritesTab';
import ContinueReadingTab from './colecao/components/ContinueReadingTab';
import StatusTab from './colecao/components/StatusTab';
import ListasTab from './colecao/components/ListasTab';
import OfflineTab from './colecao/components/OfflineTab';

const READING_STATUSES_FALLBACK = [
    { id: 1, nome: 'Vou ler' },
    { id: 2, nome: 'Lendo' },
    { id: 3, nome: 'Lido' },
    { id: 4, nome: 'Dropado' },
];

export default function ColecaoPage() {
    const { isAuthenticated, user, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const activeTab = searchParams.get('aba') || 'favorites';
    const currentPage = parseInt(searchParams.get('pagina') || '1');
    const formatoId = useMemo(() => {
        const f = searchParams.get('formato');
        if (f) {
            const n = Number(f);
            if (!Number.isNaN(n) && Number.isInteger(n) && n > 0) return n;
        }
        return 5;
    }, [searchParams]);

    const setFormato = useCallback(
        (id: number) => {
            const params = new URLSearchParams(searchParams.toString());
            params.set('formato', String(id));
            params.delete('pagina');
            navigate(`${location.pathname}?${params.toString()}`);
        },
        [searchParams, location.pathname, navigate],
    );

    const showFormatoPicker = activeTab !== 'listas' && activeTab !== 'offline';

    const [counts, setCounts] = useState<Record<string, number>>({});
    const [readingStatuses, setReadingStatuses] = useState<{ id: number; nome: string }[]>(READING_STATUSES_FALLBACK);

    const updateQueryParam = useCallback((key: string, value: string) => {
        const params = new URLSearchParams(searchParams.toString());
        if (value) params.set(key, value); else params.delete(key);
        if (key !== 'pagina') params.delete('pagina');
        navigate(`${location.pathname}?${params.toString()}`);
    }, [searchParams, location.pathname, navigate]);

    const refreshListasCount = useCallback(async () => {
        if (!isAuthenticated || !user?.usr_id) return;
        try {
            const listasRes = await api.get('listas');
            const listasPayload = unwrapApiResponse<unknown>(listasRes);
            const listasArr = Array.isArray(listasPayload)
                ? listasPayload
                : listasPayload && typeof listasPayload === 'object' && Array.isArray((listasPayload as { data?: unknown }).data)
                  ? (listasPayload as { data: unknown[] }).data
                  : [];
            setCounts((c) => ({ ...c, listas: listasArr.length }));
        } catch {
            /* ignora */
        }
    }, [isAuthenticated, user?.usr_id]);

    const listasTabProps = useMemo(() => ({ onListasChanged: refreshListasCount }), [refreshListasCount]);

    useEffect(() => {
        const loadCounts = async () => {
            if (!isAuthenticated || !user?.usr_id) return;
            try {
                const [countsRes, listasRes] = await Promise.all([
                    api.get('readings/counts'),
                    api.get('listas'),
                ]);
                const data = unwrapApiResponse<Record<string, number>>(countsRes);
                const base = data && typeof data === 'object' ? { ...data } : {};
                const listasPayload = unwrapApiResponse<unknown>(listasRes);
                const listasArr = Array.isArray(listasPayload)
                    ? listasPayload
                    : listasPayload && typeof listasPayload === 'object' && Array.isArray((listasPayload as { data?: unknown }).data)
                      ? (listasPayload as { data: unknown[] }).data
                      : [];
                base.listas = listasArr.length;
                setCounts(base);
            } catch {
                /* mantém contagens anteriores em falha parcial */
            }
        };
        if (isAuthenticated) loadCounts();
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const loadStatuses = async () => {
            try {
                const res = await api.get('readings/statuses');
                const list = unwrapApiResponse<unknown>(res);
                const arr = Array.isArray(list) ? list : (list as any)?.data;
                if (!Array.isArray(arr) || arr.length === 0) return;
                const mapped = arr
                    .map((s: any) => ({
                        id: Number(s.stt_id ?? s.id),
                        nome: String(s.stt_nome ?? s.nome ?? '').trim(),
                    }))
                    .filter((s) => s.id > 0 && s.nome);
                if (mapped.length > 0) setReadingStatuses(mapped);
            } catch {
                /* mantém fallback */
            }
        };
        loadStatuses();
    }, [isAuthenticated]);

    if (isLoading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" /></div>;

    if (!isAuthenticated) return (
        <div className="min-h-screen bg-black">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 py-32 text-center">
                <div className="w-24 h-24 bg-neutral-900 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-neutral-800"><Settings2 className="w-12 h-12 text-gray-600" /></div>
                <h1 className="text-4xl font-black text-white mb-4">Minha Coleção</h1>
                <p className="text-gray-400 text-lg mb-10 max-w-md mx-auto">Faça login para gerenciar seus favoritos, histórico de leitura e listas personalizadas.</p>
                <button onClick={() => navigate('/entrar')} className="px-10 py-4 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-xl shadow-orange-900/40">Entrar na minha conta</button>
            </div>
            <MobileBottomNav />
        </div>
    );

    const renderActiveTab = () => {
        const status = readingStatuses.find((s) => s.nome.toLowerCase() === activeTab);
        if (status) return <StatusTab statusId={status.id} statusNome={status.nome} currentPage={currentPage} />;
        switch (activeTab) {
            case 'favorites': return <FavoritesTab currentPage={currentPage} />;
            case 'continue-reading': return <ContinueReadingTab currentPage={currentPage} />;
            case 'listas': return <ListasTab {...listasTabProps} />;
            case 'offline': return <OfflineTab />;
            default: return <FavoritesTab currentPage={currentPage} />;
        }
    };
    const statusIcons: Record<string, ReactNode> = {
        lendo: <Play size={16} fill="currentColor" />,
        'vou ler': <BookOpen size={16} />,
        lido: <Check size={16} />,
        dropado: <Trash2 size={16} />,
    };

    return (
        <div className="min-h-screen bg-black text-white pb-24 md:pb-10 selection:bg-orange-500/30 selection:text-orange-500">
            <Navbar />
            <main className="max-w-7xl mx-auto px-4 pt-4 lg:pt-36">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 px-2">
                    <div className="flex items-center gap-4">
                        <div className="w-1.5 h-10 bg-orange-600 rounded-full" />
                        <div>
                            <h1 className="text-2xl font-black uppercase italic tracking-tighter text-white md:text-4xl">Minha Coleção</h1>
                            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.3em] text-gray-500">Organize sua jornada de leitura</p>
                        </div>
                    </div>
                </div>
                <div className="mb-6 md:hidden">
                    <div className="flex overflow-x-auto no-scrollbar gap-1 p-1.5 bg-neutral-900/50 backdrop-blur-xl border border-neutral-800 rounded-2xl sticky top-0 z-40">
                        <button onClick={() => updateQueryParam('aba', 'favorites')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${activeTab === 'favorites' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white hover:bg-neutral-800/50'}`}><Heart size={16} className={activeTab === 'favorites' ? 'fill-white' : ''} /> Favoritos</button>
                        <button onClick={() => updateQueryParam('aba', 'continue-reading')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${activeTab === 'continue-reading' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white hover:bg-neutral-800/50'}`}><BookOpen size={16} /> Continue lendo</button>
                        <div className="w-[1px] h-6 bg-neutral-800 my-auto mx-1" />
                        {readingStatuses.map((status) => {
                            const statusKey = status.nome.toLowerCase();
                            return (
                                <button key={status.id} onClick={() => updateQueryParam('aba', statusKey)} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${activeTab === statusKey ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white hover:bg-neutral-800/50'}`}>{statusIcons[statusKey] || <BookOpen size={16} />} {status.nome} {counts[`status_${status.id}`] > 0 && <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${activeTab === statusKey ? 'bg-white/20 text-white' : 'bg-neutral-800 text-gray-500'}`}>{counts[`status_${status.id}`]}</span>}</button>
                            );
                        })}
                        <div className="w-[1px] h-6 bg-neutral-800 my-auto mx-1" />
                        <button onClick={() => updateQueryParam('aba', 'listas')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${activeTab === 'listas' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white hover:bg-neutral-800/50'}`}><List size={16} /> Listas {counts.listas > 0 && <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${activeTab === 'listas' ? 'bg-white/20 text-white' : 'bg-neutral-800 text-gray-500'}`}>{counts.listas}</span>}</button>
                        <button onClick={() => updateQueryParam('aba', 'offline')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${activeTab === 'offline' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white hover:bg-neutral-800/50'}`}><Download size={16} /> Offline</button>
                    </div>
                </div>
                <div className="grid items-start gap-6 md:grid-cols-[240px_minmax(0,1fr)]">
                    <aside className="sticky top-24 hidden rounded-2xl border border-neutral-800 bg-neutral-900/50 p-2 backdrop-blur-xl md:block">
                        <div className="space-y-1">
                            <button onClick={() => updateQueryParam('aba', 'favorites')} className={`flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${activeTab === 'favorites' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'}`}><Heart size={16} className={activeTab === 'favorites' ? 'fill-white' : ''} /> Favoritos</button>
                            <button onClick={() => updateQueryParam('aba', 'continue-reading')} className={`flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${activeTab === 'continue-reading' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'}`}><BookOpen size={16} /> Continue lendo</button>
                            <div className="my-2 h-px bg-neutral-800" />
                            {readingStatuses.map((status) => {
                                const statusKey = status.nome.toLowerCase();
                                return (
                                    <button key={status.id} onClick={() => updateQueryParam('aba', statusKey)} className={`flex w-full items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${activeTab === statusKey ? 'bg-orange-600 text-white' : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'}`}>
                                        <span className="flex items-center gap-2">{statusIcons[statusKey] || <BookOpen size={16} />} {status.nome}</span>
                                        {counts[`status_${status.id}`] > 0 && <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${activeTab === statusKey ? 'bg-white/20 text-white' : 'bg-neutral-800 text-gray-500'}`}>{counts[`status_${status.id}`]}</span>}
                                    </button>
                                );
                            })}
                            <div className="my-2 h-px bg-neutral-800" />
                            <button onClick={() => updateQueryParam('aba', 'listas')} className={`flex w-full items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${activeTab === 'listas' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'}`}>
                                <span className="flex items-center gap-2"><List size={16} /> Listas</span>
                                {counts.listas > 0 && <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${activeTab === 'listas' ? 'bg-white/20 text-white' : 'bg-neutral-800 text-gray-500'}`}>{counts.listas}</span>}
                            </button>
                            <button onClick={() => updateQueryParam('aba', 'offline')} className={`flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-all ${activeTab === 'offline' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:bg-neutral-800/60 hover:text-white'}`}><Download size={16} /> Offline</button>
                        </div>
                    </aside>
                    <div className="pb-20 min-w-0">
                        {showFormatoPicker ? (
                            <div className="mb-6">
                                <FormatoPicker onSelect={setFormato} selectedFormato={formatoId} />
                            </div>
                        ) : null}
                        {renderActiveTab()}
                    </div>
                </div>
            </main>
            <MobileBottomNav />
        </div>
    );
}
